import React, { useContext, useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import AppContext from '../contexts/AppContext';
import ThemeContext from '../contexts/ThemeContext';
import PreferencesSection from '../components/PreferencesSection';
import { getPreferredInterpreters } from '../services/interpretersService';
import {
  CardContainer,
  TwoColumnLayout,
  SectionTitle,
  FormContent,
  StepContainer
} from '../components/PrebookingSharedComponents';
import i18n from '../i18n';
import { getActiveContractsFromUser, getContractById } from '../utils/contractUtils';
import { getLanguageList } from '../services/languageService';

/**
 * PrebookingPreferencesStep - Step 2 of the prebooking wizard
 * Allows users to specify their interpreter preferences
 */
const PrebookingPreferencesStep = ({
  formData,
  updateFields,
  onValidation,
}) => {
  const { user } = useContext(AppContext);
  const { colors } = useContext(ThemeContext);

  // Search state for interpreters
  const [preferredSearchResults, setPreferredSearchResults] = useState([]);
  const [excludedSearchResults, setExcludedSearchResults] = useState([]);
  const [preferredLoading, setPreferredLoading] = useState(false);
  const [excludedLoading, setExcludedLoading] = useState(false);

  // Cache interpreter list — fetch once, filter client-side
  const interpretersCacheRef = useRef(null);
  const interpretersFetchPromiseRef = useRef(null);

  useEffect(() => {
    // Pre-fetch interpreters on mount
    if (!interpretersCacheRef.current && !interpretersFetchPromiseRef.current) {
      interpretersFetchPromiseRef.current = getPreferredInterpreters()
        .then(result => {
          interpretersCacheRef.current = result;
          return result;
        })
        .catch(error => {
          console.error('Failed to pre-fetch interpreters:', error);
          interpretersFetchPromiseRef.current = null;
        });
    }
  }, []);

  // Determine booking type
  const isOnsiteBooking = formData.presence === 'onsite';
  const isAnnouncementBooking = formData.presence === 'notice';

  const availableContracts = React.useMemo(() => getActiveContractsFromUser(user), [user]);
  const selectedContract = React.useMemo(() => {
    const fromSelection = getContractById(availableContracts, formData.selectedContractId);
    return fromSelection?.raw || availableContracts[0]?.raw || null;
  }, [availableContracts, formData.selectedContractId]);

  // Get contract distance limit (pbOnSiteKmLimit) for on-site bookings
  const contractDistanceLimit = selectedContract?.pbOnSiteKmLimit || 0;

  // Determine if there are alternative languages available for the selected language
  const selectedLanguage = formData.selectableLanguages?.find(lang => lang.id === formData.selectedLanguageId);
  const [alternativeLanguageObjects, setAlternativeLanguageObjects] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const fetchAlternativeLanguages = async () => {
      try {
        if (!formData.selectedLanguageId) {
          return;
        }
        
        const orderTime = formData.timeSlots?.[0]?.date;
        let isoTime = null;
        if (orderTime) {
           // We use the first time slot to determine the order time for alternative languages
           const timeSlot = formData.timeSlots?.[0];
           const combinedDateTime = new Date(timeSlot.date);
           if (timeSlot.startTime) {
             combinedDateTime.setHours(timeSlot.startTime.getHours());
             combinedDateTime.setMinutes(timeSlot.startTime.getMinutes());
             combinedDateTime.setSeconds(0);
             combinedDateTime.setMilliseconds(0);
           }
           isoTime = combinedDateTime.toISOString();
        }
        
        const options = {
          prebooked: true,
          presence: formData.presence
        };
        if (isoTime) {
          options.orderTime = isoTime;
        }
        
        const json = await getLanguageList(options);
        if (isMounted) {
          const mapping = json.alternativeLanguages || {};
          const altIds = mapping[formData.selectedLanguageId] || [];
          const allLangs = json.selectableLanguages || formData.selectableLanguages || [];
          const altObjects = allLangs.filter(lang => altIds.includes(lang.id)) || [];
          setAlternativeLanguageObjects(altObjects);
        }
      } catch (error) {
        console.error('Failed to fetch alternative languages:', error);
      }
    };
    fetchAlternativeLanguages();
    return () => { isMounted = false; };
  }, [formData.selectedLanguageId, formData.presence, formData.timeSlots, formData.selectableLanguages]);

  // Get skills for selected language from formData
  // Skills are used for gender preferences and (in non-staging environments) for competence levels
  const skills = formData.selectedLanguageId && formData.selectableSkills && formData.selectableSkills[formData.selectedLanguageId]
    ? formData.selectableSkills[formData.selectedLanguageId]
    : [];

  // Get contract allowed levels filtered by interpretation type
  const contractAllowedLevels = React.useMemo(() => {
    const allLevels = selectedContract?.allowedLevels || [];
    let typeCode = 'prebooking_remote';
    if (formData.presence === 'onsite') typeCode = 'prebooking_onsite';
    else if (formData.presence === 'notice') typeCode = 'prebooking_notice';
    else if (formData.presence === 'remote' && (formData.mode === 'conference' || formData.mode === 'call')) typeCode = 'prebooking_external';
    return allLevels
      .filter(l => l.interpretationTypeCode === typeCode)
      .map(l => ({
        id: l.levelId,
        name: l.levelName,
        interpreterTier: l.interpreterTier,
        priority: l.interpreterTier,
      }));
  }, [selectedContract?.allowedLevels, formData.presence, formData.mode]);

  const levelsRankOrdering = selectedContract?.levelsRankOrdering || 'minimum_best';

  const sortedLevels = React.useMemo(() => {
    return [...contractAllowedLevels].sort((a, b) =>
      levelsRankOrdering === 'maximum_best'
        ? a.interpreterTier - b.interpreterTier
        : b.interpreterTier - a.interpreterTier
    );
  }, [contractAllowedLevels, levelsRankOrdering]);

  // Get useInterpreterTier from Business Type (not from levels array length)
  // This prevents configuration errors from being silently hidden
  const useInterpreterTier = React.useMemo(() => {
    const businessType = user?.userGroup?.businessType || user?.businessTypes?.[0];
    return businessType?.useInterpreterTier === true;
  }, [user?.userGroup?.businessType, user?.businessTypes]);

  // Handle preferences change from PreferencesSection
  const handlePreferencesChange = useCallback((newPreferences) => {
    // Update formData with new preferences
    const updatedFields = {
      // Map our component field names to formData field names
      gender: newPreferences.gender,
      experienceSkill: newPreferences.experienceSkill,
      onsiteFallbackHandling: newPreferences.onsiteFallbackHandling,
      acceptBeyondKmLimit: newPreferences.acceptBeyondKmLimit,
      kmBeyondLimit: newPreferences.kmBeyondLimit,
      acceptAlternativeLanguage: newPreferences.acceptAlternativeLanguage,
      // If there are multiple alternative languages available, we currently only support
      // selecting the first one, as the backend `alternativeLanguageId` field expects a single UUID.
      alternativeLanguageId: alternativeLanguageObjects?.[0]?.id || null,
      preferredInterpreters: newPreferences.preferredInterpreters?.map(i => i.id) || [],
      excludedInterpreters: newPreferences.excludedInterpreters?.map(i => i.id) || [],
      additionalPreferencesExpanded: newPreferences.additionalPreferencesExpanded,
      // Pass contract distance limit for transformer to use
      contractDistanceLimit,
      // Soft limit preferences
      backupCallTypeForOnSite: newPreferences.backupCallTypeForOnSite,
    };

    // Save competenceLevel when levels are available
    if (useInterpreterTier) {
      updatedFields.competenceLevel = newPreferences.competenceLevels || [];
      // Pass tier metadata for transformer to sort at submission
      const tierMap = {};
      contractAllowedLevels.forEach(l => { tierMap[l.id] = l.interpreterTier; });
      updatedFields.competenceLevel = [...newPreferences.competenceLevels].sort((a, b) => {                                                              
        const tierA = tierMap[a] ?? 999;                                                                                                
        const tierB = tierMap[b] ?? 999;                                                                                                
        return levelsRankOrdering === 'maximum_best'                                                                                    
          ? tierA - tierB                                                                                                               
          : tierB - tierA;                                                                                                              
      }); 
    }

    updateFields(updatedFields);
  }, [updateFields, useInterpreterTier, contractDistanceLimit, alternativeLanguageObjects, contractAllowedLevels, levelsRankOrdering]);

  // Handle validation from PreferencesSection
  const handleValidation = useCallback((valid) => {
    // Expose validation state to parent wizard if callback provided
    if (onValidation) {
      onValidation(valid);
    }
  }, [onValidation]);

  // Get cached interpreters (fetch once if not yet available)
  const getCachedInterpreters = useCallback(async () => {
    if (interpretersCacheRef.current) {
      return interpretersCacheRef.current;
    }
    if (interpretersFetchPromiseRef.current) {
      return interpretersFetchPromiseRef.current;
    }

    interpretersFetchPromiseRef.current = getPreferredInterpreters()
      .then(result => {
        interpretersCacheRef.current = result;
        return result;
      });
    return interpretersFetchPromiseRef.current;
  }, []);

  const filterInterpreters = useCallback((interpreters, query) => {
    return interpreters
      .filter(interpreter =>
        interpreter.name?.toLowerCase().includes(query.toLowerCase()) ||
        interpreter.username?.toLowerCase().includes(query.toLowerCase())
      )
      .slice(0, 10)
      .map(interpreter => ({
        id: interpreter.id,
        name: interpreter.name || interpreter.username,
      }));
  }, []);

  // Search for preferred interpreters — uses cached list
  const handleSearchPreferred = useCallback(async (query) => {
    if (query.length < 2) {
      setPreferredSearchResults([]);
      return;
    }

    setPreferredLoading(true);
    try {
      const interpreters = await getCachedInterpreters();
      setPreferredSearchResults(filterInterpreters(interpreters, query));
    } catch (error) {
      console.error('Failed to search interpreters:', error);
      setPreferredSearchResults([]);
    } finally {
      setPreferredLoading(false);
    }
  }, [getCachedInterpreters, filterInterpreters]);

  // Search for excluded interpreters — uses same cached list
  const handleSearchExcluded = useCallback(async (query) => {
    if (query.length < 2) {
      setExcludedSearchResults([]);
      return;
    }

    setExcludedLoading(true);
    try {
      const interpreters = await getCachedInterpreters();
      setExcludedSearchResults(filterInterpreters(interpreters, query));
    } catch (error) {
      console.error('Failed to search interpreters:', error);
      setExcludedSearchResults([]);
    } finally {
      setExcludedLoading(false);
    }
  }, [getCachedInterpreters, filterInterpreters]);

  // Convert formData to PreferencesSection format
  const getInitialPreferences = useCallback(() => {
    // Pre-select all available levels when none are selected
    let defaultCompetenceLevels = [];
    if (useInterpreterTier) {
      if (formData.competenceLevel && formData.competenceLevel.length > 0) {
        defaultCompetenceLevels = formData.competenceLevel;
      } else {
        // Pre-select all visible levels from contract
        defaultCompetenceLevels = contractAllowedLevels.map(level => level.id);
      }
    }

    return {
      gender: formData.gender || 'does_not_matter',
      competenceLevels: defaultCompetenceLevels,
      experienceSkill: formData.experienceSkill || 'does_not_matter_experience',
      onsiteFallbackHandling: formData.onsiteFallbackHandling || 'assign_remote_interpreter',
      acceptBeyondKmLimit: formData.acceptBeyondKmLimit || false,
      kmBeyondLimit: formData.kmBeyondLimit,
      acceptAlternativeLanguage: formData.acceptAlternativeLanguage || false,
      preferredInterpreters: formData.preferredInterpreters?.map(id => ({ id, name: `Interpreter ${id}` })) || [],
      excludedInterpreters: formData.excludedInterpreters?.map(id => ({ id, name: `Interpreter ${id}` })) || [],
      additionalPreferencesExpanded: formData.additionalPreferencesExpanded || false,
      // Soft limit preferences
      backupCallTypeForOnSite: formData.backupCallTypeForOnSite || 'call',
    };
  }, [formData, useInterpreterTier, contractAllowedLevels]);

  return (
    <StepContainer colors={colors}>
      <CardContainer>
        <TwoColumnLayout>
          <SectionTitle colors={colors}>{i18n('preferences')}</SectionTitle>
          <FormContent>
            <PreferencesSection
              isOnsiteBooking={isOnsiteBooking}
              isAnnouncementBooking={isAnnouncementBooking}
              selectedLanguage={selectedLanguage}
              alternativeLanguageObjects={alternativeLanguageObjects}
              initialPreferences={getInitialPreferences()}
              onChange={handlePreferencesChange}
              onValidation={handleValidation}
              showTitle={false}
              // Pass search handlers and results
              preferredSearchResults={preferredSearchResults}
              excludedSearchResults={excludedSearchResults}
              preferredLoading={preferredLoading}
              excludedLoading={excludedLoading}
              onSearchPreferred={handleSearchPreferred}
              onSearchExcluded={handleSearchExcluded}
              // Pass backend data (skills already filtered by selectedLanguageId in parent)
              levels={sortedLevels}
              useInterpreterTier={useInterpreterTier}
              skills={skills}
              levelsRankOrdering={levelsRankOrdering}
              // Pass contract distance limit for on-site bookings
              contractDistanceLimit={contractDistanceLimit}
            />
          </FormContent>
        </TwoColumnLayout>
      </CardContainer>
    </StepContainer>
  );
};

PrebookingPreferencesStep.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFields: PropTypes.func.isRequired,
  onValidation: PropTypes.func,
};

export default PrebookingPreferencesStep;
